<template>
  <div class="profile-page-wrapper">
    <!-- Topbar Header (Standard SmartFix Dashboard Header) -->
    <div class="dashboard-topbar">
      <div class="dashboard-title-group">
        <h4 class="dashboard-page-title">
          <CIcon name="cil-user" class="dashboard-title-icon" />
          <span>{{ $t('profile.title') }}</span>
        </h4>
        <p class="dashboard-topbar-subtitle">{{ $t('profile.subtitle') }}</p>
      </div>
    </div>

    <!-- Main Content Area -->
    <div class="profile-content-container">
      <!-- Unified Profile Card (Left: Identity, Right: Personal Details) -->
      <CCard class="details-card profile-unified-card mb-4">
        <div class="profile-unified-body">
          <!-- Left Identity Column (~34%) -->
          <div class="profile-identity-col">
            <div class="profile-avatar-wrapper" v-keyboard-activate role="button" tabindex="0" aria-haspopup="dialog" :aria-expanded="String(showAvatarModal)" :aria-label="$t('profile.changeAvatar')" @click="openAvatarModal" :title="$t('profile.changeAvatar')">
              <img :src="userAvatar" class="profile-avatar-img" :alt="displayName" />
              <span class="profile-online-badge" :title="$t('profile.statusActive')"></span>
              <div class="profile-avatar-edit-overlay" :title="$t('profile.changeAvatar')">
                <CIcon name="cil-camera" />
              </div>
            </div>

            <h3 class="profile-name">{{ displayName }}</h3>

            <div class="profile-active-status">
              <CIcon name="cil-check-circle" class="mr-1 text-success" />
              <span class="text-success font-weight-bold">{{ $t('profile.statusActive') }}</span>
            </div>
          </div>

          <!-- Right Details Column (~66%) -->
          <div class="profile-info-col">
            <div class="profile-info-header d-flex justify-content-between align-items-center">
              <h5 class="profile-info-title mb-0">
                <CIcon name="cil-address-book" class="mr-2" />
                <span>{{ $t('profile.personalInfo') }}</span>
              </h5>
            </div>

            <div class="profile-details-grid">
              <!-- Full Name -->
              <div class="profile-detail-cell">
                <span class="profile-detail-label">{{ $t('profile.fullName') }}</span>
                <strong class="profile-detail-value">{{ displayName }}</strong>
              </div>

              <!-- Email -->
              <div class="profile-detail-cell">
                <span class="profile-detail-label">{{ $t('profile.email') }}</span>
                <strong class="profile-detail-value text-break">{{ userEmail }}</strong>
              </div>

              <!-- Phone Number -->
              <div class="profile-detail-cell">
                <div class="d-flex justify-content-between align-items-center mb-1">
                  <span class="profile-detail-label mb-0">{{ $t('profile.phone') }}</span>
                  <button
                    type="button"
                    class="btn-edit-ghost"
                    @click="openPhoneModal"
                    :title="$t('profile.editPhone')"
                  >
                    <CIcon name="cil-pencil" class="icon-edit-ghost" />
                  </button>
                </div>
                <strong class="profile-detail-value">{{ userPhone }}</strong>
              </div>

              <!-- System Role -->
              <div class="profile-detail-cell">
                <span class="profile-detail-label">{{ $t('profile.role') }}</span>
                <strong class="profile-detail-value">{{ roleDisplayName }}</strong>
              </div>

              <!-- Affiliation / School (User) -->
              <div v-if="affiliationDisplay" class="profile-detail-cell">
                <span class="profile-detail-label">{{ $t('profile.affiliation') }}</span>
                <strong class="profile-detail-value">{{ affiliationDisplay }}</strong>
              </div>

              <!-- Department (Staff) -->
              <div v-if="departmentName" class="profile-detail-cell">
                <span class="profile-detail-label">{{ $t('profile.department') }}</span>
                <strong class="profile-detail-value">{{ departmentName }}</strong>
              </div>

              <!-- Section (Staff) -->
              <div v-if="sectionName" class="profile-detail-cell">
                <span class="profile-detail-label">{{ $t('profile.section') }}</span>
                <strong class="profile-detail-value">{{ sectionName }}</strong>
              </div>

              <!-- Job Type (Technician) -->
              <div v-if="jobTypeName" class="profile-detail-cell">
                <span class="profile-detail-label">{{ $t('profile.jobType') }}</span>
                <strong class="profile-detail-value text-primary">{{ jobTypeName }}</strong>
              </div>

              <!-- Account Type -->
              <div class="profile-detail-cell">
                <span class="profile-detail-label">{{ $t('profile.accountType') }}</span>
                <strong class="profile-detail-value">
                  {{ isExternal ? $t('profile.accountTypeExternal') : $t('profile.accountTypeMfu') }}
                </strong>
              </div>
            </div>
          </div>
        </div>
      </CCard>
    </div>

    <!-- Edit Phone Number Modal -->
    <CModal
      :title="$t('profile.editPhoneTitle')"
      :show.sync="showPhoneModal"
      size="md"
      centered
      class="profile-edit-modal"
    >
      <div class="p-3">
        <div class="form-group mb-0">
          <label class="font-weight-bold">{{ $t('profile.phone') }} <span class="text-danger">*</span></label>
          <div class="input-group">
            <div class="input-group-prepend">
              <span class="input-group-text"><CIcon name="cil-phone" /></span>
            </div>
            <input
              v-model.trim="editPhone"
              type="tel"
              class="form-control"
              placeholder="เช่น 081-234-5678"
              maxlength="20"
              @keyup.enter="savePhone"
            />
          </div>
          <small class="form-text text-muted mt-2">
            <CIcon name="cil-info" class="mr-1 text-primary" />
            {{ $t('profile.phoneNote') }}
          </small>
          <p v-if="saveError" class="text-danger" role="alert">{{ $t(saveError) }}</p>
        </div>
      </div>

      <template #footer>
        <CButton color="secondary" @click="showPhoneModal = false">
          {{ $t('profile.cancelBtn') }}
        </CButton>
        <CButton color="primary" class="btn-save-modal" :disabled="isSaving" @click="savePhone">
          <CIcon name="cil-check" class="mr-1" />
          {{ isSaving ? '...' : $t('profile.saveBtn') }}
        </CButton>
      </template>
    </CModal>

    <!-- Change Avatar Modal -->
    <CModal
      :title="$t('profile.changeAvatarTitle')"
      :show.sync="showAvatarModal"
      size="lg"
      centered
      class="profile-edit-modal"
    >
      <div class="p-3">
        <div class="avatar-edit-box text-center mb-2">
          <ImageAttachment
            :preview="editAvatar ? editAvatarPreview : ''"
            :disabled="isSaving"
            @change="handleAvatarFileChange"
            @remove="clearAvatar"
          />
          <small class="text-muted d-block mt-2">{{ $t('profile.avatarSizeLimit') }}</small>

          <!-- Preset Avatars -->
          <div class="preset-avatars-section mt-3">
            <span class="text-muted small font-weight-bold d-block mb-2">{{ $t('profile.orChoosePreset') }}</span>
            <div class="d-flex justify-content-center flex-wrap gap-2">
              <button
                v-for="num in 8"
                :key="num"
                type="button"
                class="preset-avatar-btn keyboard-action" :aria-pressed="String(isPresetActive(num))" :aria-label="$t('profile.presetAvatar', { number: num })"
                :class="{ active: isPresetActive(num) }"
                @click="choosePresetAvatar(num)"
                :title="`Preset ${num}`"
              >
                <img :src="require(`@/assets/avatars/${num}.jpg`)" class="preset-avatar-thumb" />
              </button>
            </div>
          </div>
        </div>
        <p v-if="saveError" class="text-danger" role="alert">{{ $t(saveError) }}</p>
      </div>

      <template #footer>
        <CButton color="secondary" @click="showAvatarModal = false">
          {{ $t('profile.cancelBtn') }}
        </CButton>
        <CButton color="primary" class="btn-save-modal" :disabled="isSaving" @click="saveAvatar">
          <CIcon name="cil-check" class="mr-1" />
          {{ isSaving ? '...' : $t('profile.saveBtn') }}
        </CButton>
      </template>
    </CModal>
  </div>
</template>

<script>
import keyboardActivate from '@/directives/keyboardActivate';
import CModal from '@/components/common/AccessibleModal.vue';
import ImageAttachment from "@/components/common/ImageAttachment.vue";
import { mapGetters } from "vuex";
import Service from "@/service/api";
import Swal from "sweetalert2";

export default {
  directives: { keyboardActivate },
  name: "ProfilePage",
  components: { CModal, ImageAttachment },
  data() {
    return {
      showPhoneModal: false,
      showAvatarModal: false,
      showEditModal: false,
      isSaving: false,
      saveError: '',
      editPhone: "",
      editAvatar: "",
      orgUnits: [],
      editForm: {
        name: "",
        surname: "",
        phone: "",
        affiliationId: "",
        avatar: "",
      },
    };
  },
  computed: {
    ...mapGetters("auth", ["profile", "role"]),
    currentRole() {
      return (this.profile?.role || this.role || "user").toLowerCase();
    },
    displayName() {
      if (!this.profile) return "User";
      const full = `${this.profile.name || ""} ${this.profile.surname || ""}`.trim();
      return full || this.profile.username || "User";
    },
    userEmail() {
      return this.profile?.email || "-";
    },
    userPhone() {
      const phone = this.profile?.mobile_phone || this.profile?.phone || this.profile?.tel;
      return phone && String(phone).trim() ? String(phone).trim() : "-";
    },
    userAvatar() {
      const a = this.profile?.avatar;
      if (!a) return require("@/assets/avatars/8.jpg");
      if (typeof a === "string" && a.startsWith("preset:")) {
        const num = a.replace("preset:", "");
        try {
          return require(`@/assets/avatars/${num}.jpg`);
        } catch (e) {
          return require("@/assets/avatars/8.jpg");
        }
      }
      return a;
    },
    editAvatarPreview() {
      const a = this.editAvatar || this.editForm.avatar;
      if (!a) return this.userAvatar;
      if (typeof a === "string" && a.startsWith("preset:")) {
        const num = a.replace("preset:", "");
        try {
          return require(`@/assets/avatars/${num}.jpg`);
        } catch (e) {
          return require("@/assets/avatars/8.jpg");
        }
      }
      return a;
    },
    roleDisplayName() {
      const isEn = this.$store?.state?.locale?.lang === "en";
      const r = this.currentRole;
      const mapTh = {
        user: "ผู้ใช้งานทั่วไป",
        technician: "ช่างเทคนิค",
        supervisor: "หัวหน้างาน",
        admin: "ผู้ดูแลระบบ",
        student_affairs: "ส่วนพัฒนานักศึกษา",
      };
      const mapEn = {
        user: "User",
        technician: "Technician",
        supervisor: "Supervisor",
        admin: "Admin",
        student_affairs: "Student Affairs",
      };
      return (isEn ? mapEn[r] : mapTh[r]) || (isEn ? "User" : "ผู้ใช้งานทั่วไป");
    },
    roleIcon() {
      switch (this.currentRole) {
        case "admin":
          return "cil-shield-alt";
        case "supervisor":
          return "cil-user-follow";
        case "technician":
          return "cil-wrench";
        default:
          return "cil-user";
      }
    },
    roleBadgeClass() {
      return `badge-${this.currentRole}`;
    },
    affiliationName() {
      if (!this.profile) return "";
      const aff = this.profile.affiliation;
      if (aff && typeof aff === "object") {
        return aff.name || "";
      }
      return aff || "";
    },
    affiliationDisplay() {
      const name = this.affiliationName;
      if (!name) return "";
      const isEn = this.$store?.state?.locale?.lang === "en";
      if (isEn) {
        if (name === "นักศึกษา") return "Student";
        if (name === "บุคลากร") return "Staff";
        if (name === "อาจารย์") return "Lecturer";
      }
      return name;
    },
    departmentName() {
      if (!this.profile) return "";
      const dept = this.profile.department;
      if (dept && typeof dept === "object") {
        return dept.name || "";
      }
      return dept || "";
    },
    sectionName() {
      if (!this.profile) return "";
      const sec = this.profile.section;
      if (sec && typeof sec === "object") {
        return sec.departmentName || sec.name || "";
      }
      return sec || "";
    },
    jobTypeName() {
      if (!this.profile) return "";
      const jt = this.profile.jobType;
      if (jt && typeof jt === "object") {
        return jt.name || "";
      }
      return jt || "";
    },
    isExternal() {
      return this.profile?.isExternalUser === true;
    },
  },
  methods: {
    openPhoneModal() {
      if (this.isSaving) return;
      this.saveError = '';
      const phone = this.profile?.mobile_phone || this.profile?.phone || this.profile?.tel || "";
      this.editPhone = phone ? String(phone).trim() : "";
      this.editForm.phone = this.editPhone;
      this.showPhoneModal = true;
    },

    openAvatarModal() {
      if (this.isSaving) return;
      this.saveError = '';
      this.editAvatar = this.profile?.avatar || "";
      this.editForm.avatar = this.editAvatar;
      this.showAvatarModal = true;
    },

    triggerAvatarPicker() {
      if (this.$refs.avatarFileInput) {
        this.$refs.avatarFileInput.click();
      }
    },

    async handleAvatarFileChange(e) {
      const file = e.target?.files?.[0];
      if (!file) return;

      try {
        const compressedDataUrl = await this.compressImage(file, 300, 300, 0.85);
        this.editAvatar = compressedDataUrl;
        this.editForm.avatar = compressedDataUrl;
      } catch (err) {
        console.error("Avatar compression fallback:", err);
        const reader = new FileReader();
        reader.onload = (ev) => {
          this.editAvatar = ev.target.result;
          this.editForm.avatar = ev.target.result;
        };
        reader.readAsDataURL(file);
      }
    },

    compressImage(file, maxWidth = 300, maxHeight = 300, quality = 0.85) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (event) => {
          const img = new Image();
          img.onload = () => {
            let width = img.width;
            let height = img.height;

            if (width > height) {
              if (width > maxWidth) {
                height = Math.round((height * maxWidth) / width);
                width = maxWidth;
              }
            } else {
              if (height > maxHeight) {
                width = Math.round((width * maxHeight) / height);
                height = maxHeight;
              }
            }

            const canvas = document.createElement("canvas");
            canvas.width = width;
            canvas.height = height;
            const ctx = canvas.getContext("2d");
            ctx.drawImage(img, 0, 0, width, height);
            resolve(canvas.toDataURL("image/jpeg", quality));
          };
          img.onerror = reject;
          img.src = event.target.result;
        };
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
    },

    choosePresetAvatar(num) {
      this.editAvatar = `preset:${num}`;
      this.editForm.avatar = `preset:${num}`;
    },

    isPresetActive(num) {
      const current = this.editAvatar || this.editForm.avatar;
      if (!current) {
        return num === 1;
      }
      return current === `preset:${num}`;
    },

    clearAvatar() {
      this.editAvatar = "";
      this.editForm.avatar = "";
      if (this.$refs.avatarFileInput) {
        this.$refs.avatarFileInput.value = "";
      }
    },

    async openEditModal() {
      const name = (this.profile?.name || "").trim();
      const surname = (this.profile?.surname || "").trim();
      const phone = this.profile?.mobile_phone || this.profile?.phone || this.profile?.tel || "";
      const currentAffiliationId = typeof this.profile?.affiliation === "object"
        ? this.profile.affiliation?._id
        : this.profile?.affiliation;

      this.editPhone = phone ? String(phone).trim() : "";
      this.editAvatar = this.profile?.avatar || "";
      this.editForm = {
        name: name || (this.profile?.username || ""),
        surname: surname || "",
        phone: this.editPhone,
        affiliationId: currentAffiliationId || "",
        avatar: this.editAvatar,
      };

      if (this.currentRole === "user" && this.orgUnits.length === 0) {
        try {
          const res = await Service.user("get", null, { urlAppend: "/organization-units" });
          if (res.data?.success && Array.isArray(res.data.data)) {
            this.orgUnits = res.data.data;
          }
        } catch (e) {
          console.error("Error loading organization units:", e);
        }
      }

      this.showEditModal = true;
    },

    persistServerProfile(response, syncAvatar = false) {
      const updated = response.data?.data;
      if (response.status !== 200 || response.data?.success !== true ||
          !updated || typeof updated !== 'object' || Array.isArray(updated) ||
          !(updated._id || updated.id)) {
        throw new Error('Profile update was not confirmed by the server');
      }

      this.$store.commit('auth/setProfile', updated);
      if (syncAvatar) this.$store.commit('user/setAvatar', updated.avatar || null);
      try {
        localStorage.setItem('user', JSON.stringify(updated));
        if (syncAvatar) {
          if (updated.avatar) localStorage.setItem('avatar', updated.avatar);
          else localStorage.removeItem('avatar');
        }
        return true;
      } catch (error) {
        // The server has saved the profile; a cache failure must not trigger a retry.
        console.warn('Profile saved, but local cache could not be updated:', error);
        return false;
      }
    },
    showProfileSaved(titleKey, cacheSaved) {
      Swal.fire({
        icon: cacheSaved ? 'success' : 'warning',
        title: this.$t(titleKey),
        text: cacheSaved ? undefined : this.$t('profile.cacheWarning'),
        timer: cacheSaved ? 1800 : undefined,
        showConfirmButton: !cacheSaved,
      });
    },
    async savePhone() {
      if (this.isSaving) return;
      this.isSaving = true;
      this.saveError = '';
      try {
        const res = await Service.user('put', { mobile_phone: this.editPhone }, { urlAppend: '/me/profile' });
        const cacheSaved = this.persistServerProfile(res);
        this.showPhoneModal = false;
        this.showProfileSaved('profile.phoneSaved', cacheSaved);
      } catch (error) {
        this.saveError = error.response?.status === 403 ? 'mgmt.accessDenied' : 'profile.saveError';
      } finally {
        this.isSaving = false;
      }
    },
    async saveAvatar() {
      if (this.isSaving) return;
      this.isSaving = true;
      this.saveError = '';
      try {
        const avatar = this.editAvatar || this.editForm.avatar || null;
        const res = await Service.user('put', { avatar }, { urlAppend: '/me/profile' });
        const cacheSaved = this.persistServerProfile(res, true);
        this.showAvatarModal = false;
        this.showProfileSaved('profile.avatarSaved', cacheSaved);
      } catch (error) {
        this.saveError = error.response?.status === 403 ? 'mgmt.accessDenied' : 'profile.saveError';
      } finally {
        this.isSaving = false;
      }
    },

    async saveProfile() {
      if (this.isSaving) return;
      if (!this.editForm.name) {
        Swal.fire({
          icon: "warning",
          title: this.$t("login.notice") || "แจ้งเตือน",
          text: "กรุณาระบุชื่อ",
          confirmButtonColor: "#1e40af",
        });
        return;
      }

      this.isSaving = true;
      this.saveError = '';
      try {
        const payload = {
          name: this.editForm.name,
          surname: this.editForm.surname,
          mobile_phone: this.editForm.phone,
          affiliationId: this.editForm.affiliationId || undefined,
          avatar: this.editForm.avatar || null,
        };

        const res = await Service.user("put", payload, { urlAppend: "/me/profile" });
        const cacheSaved = this.persistServerProfile(res, true);
        this.showEditModal = false;
        this.showProfileSaved('profile.saveSuccess', cacheSaved);
      } catch (error) {
        this.saveError = error.response?.status === 403 ? 'mgmt.accessDenied' : 'profile.saveError';
        Swal.fire({ icon: 'error', title: this.$t(this.saveError) });
      } finally {
        this.isSaving = false;
      }
    },
  },
};
</script>

<style scoped>
.profile-page-wrapper {
  width: 100%;
  margin: 0;
  padding: 0 0 40px;
}

/* ===== Topbar Header (Standard SmartFix Style) ===== */
.dashboard-topbar {
  width: 100%;
  min-width: 0;
  min-height: 112px;
  margin: 0 0 24px;
  padding: 22px 28px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  background: #1e40af;
  box-sizing: border-box;
}

.dashboard-title-group {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
}

.dashboard-page-title {
  margin: 0 !important;
  padding: 0 !important;
  display: flex;
  align-items: center;
  gap: 12px;
  color: #ffffff !important;
  font-size: clamp(1.35rem, 2vw, 1.8rem);
  font-weight: 800;
  letter-spacing: -0.02em;
  line-height: 1.2;
}

.dashboard-title-icon {
  color: #ffffff !important;
  font-size: 1.5rem;
  width: 28px;
  height: 28px;
  flex-shrink: 0;
}

.dashboard-topbar-subtitle {
  margin: 0;
  color: rgba(255, 255, 255, 0.85);
  font-size: 0.92rem;
  font-weight: 400;
}

.dashboard-filter {
  display: flex;
  align-items: center;
  flex-shrink: 0;
}

.report-btn,
.hero-action {
  background: #ffffff !important;
  border: none !important;
  color: #1e40af !important;
  min-height: 42px !important;
  padding: 10px 22px !important;
  border-radius: 50px !important;
  font-size: 14px !important;
  font-weight: 700 !important;
  display: flex !important;
  align-items: center !important;
  gap: 8px !important;
  cursor: pointer !important;
  white-space: nowrap !important;
  flex-shrink: 0 !important;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12) !important;
  transition: all 0.2s ease !important;
}

.report-btn:hover,
.hero-action:hover {
  background: #fdfdfd !important;
  transform: translateY(-1px) !important;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2) !important;
}

/* ===== Content Container ===== */
.profile-content-container {
  max-width: 1080px;
  margin: 20px auto 0;
  padding: 0 20px;
}

/* ===== Card Base ===== */
.details-card {
  border-radius: 16px !important;
  overflow: hidden;
  border: 1px solid rgba(37, 99, 235, 0.12);
  box-shadow: 0 4px 20px rgba(37, 99, 235, 0.06);
  background: #ffffff;
}

.details-card-header {
  background: #f8fafc !important;
  border-bottom: 1px solid rgba(37, 99, 235, 0.08) !important;
  color: #1e40af !important;
  padding: 16px 22px;
}

.details-card-header h4 {
  margin-bottom: 0;
  color: #1e40af;
  font-weight: 800;
  font-size: 1.15rem;
  display: flex;
  align-items: center;
}

/* ===== 1. Unified Profile Card (Left & Right Split) ===== */
.profile-unified-card {
  border-top: 4px solid #1e40af !important;
}

.profile-unified-body {
  display: flex;
  flex-direction: row;
  align-items: stretch;
  min-height: 320px;
}

/* Left Column: Identity (~34%) */
.profile-identity-col {
  flex: 0 0 34%;
  max-width: 34%;
  padding: 36px 28px;
  background: linear-gradient(180deg, #ffffff 0%, #f8fafc 100%);
  border-right: 1px solid rgba(37, 99, 235, 0.1);
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  justify-content: center;
}

.profile-avatar-wrapper {
  position: relative;
  width: 104px;
  height: 104px;
  margin-bottom: 16px;
  border-radius: 50%;
  border: 3px solid #1e40af;
  box-shadow: 0 6px 16px rgba(37, 99, 235, 0.16);
  background: #ffffff;
  cursor: pointer;
  transition: transform 0.2s ease;
}

.profile-avatar-wrapper:hover {
  transform: scale(1.03);
}

.profile-avatar-img {
  width: 100%;
  height: 100%;
  border-radius: 50%;
  object-fit: cover;
}

.profile-avatar-edit-overlay {
  position: absolute;
  bottom: 0;
  right: 0;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: #1e40af;
  color: #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 2px solid #ffffff;
  font-size: 14px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.2);
  transition: all 0.2s ease;
}

.profile-avatar-wrapper:hover .profile-avatar-edit-overlay {
  background: #1e3a8a;
  transform: scale(1.1);
}

.profile-online-badge {
  position: absolute;
  bottom: 2px;
  left: 6px;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: #10b981;
  border: 2px solid #ffffff;
}

.profile-name {
  margin: 0 0 12px;
  font-size: 1.45rem;
  font-weight: 800;
  color: #2c3e50;
  line-height: 1.25;
  word-break: break-word;
}

.profile-active-status {
  display: inline-flex;
  align-items: center;
  padding: 5px 14px;
  border-radius: 20px;
  background: #f0fdf4;
  border: 1px solid #bbf7d0;
  font-size: 0.84rem;
}

.profile-edit-btn-pill {
  display: inline-flex;
  align-items: center;
  padding: 6px 16px;
  border-radius: 20px;
  font-size: 0.82rem;
  font-weight: 700;
  color: #1e40af;
  background: #ffffff;
  border: 1px solid rgba(37, 99, 235, 0.25);
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 2px 6px rgba(37, 99, 235, 0.08);
}

.profile-edit-btn-pill:hover {
  background: #1e40af;
  color: #ffffff;
  border-color: #1e40af;
  transform: translateY(-1px);
}

/* Right Column: Personal Details (~66%) */
.profile-info-col {
  flex: 0 0 66%;
  max-width: 66%;
  background: #ffffff;
  display: flex;
  flex-direction: column;
}

.profile-info-header {
  padding: 16px 24px;
  background: #fcfcfc;
  border-bottom: 1px solid rgba(37, 99, 235, 0.08);
}

.profile-info-title {
  margin: 0;
  font-size: 1.1rem;
  font-weight: 800;
  color: #1e40af;
  display: flex;
  align-items: center;
}

.btn-edit-header {
  background: #1e40af !important;
  border: 1px solid #1e40af !important;
  color: #ffffff !important;
  font-size: 0.84rem;
  font-weight: 700;
  border-radius: 6px;
  padding: 6px 14px;
  display: inline-flex;
  align-items: center;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 2px 5px rgba(37, 99, 235, 0.15);
}

.btn-edit-header:hover {
  background: #1e3a8a !important;
  border-color: #1e3a8a !important;
  transform: translateY(-1px);
  box-shadow: 0 4px 10px rgba(37, 99, 235, 0.25);
}

.btn-edit-ghost {
  background: transparent !important;
  border: none !important;
  color: #94a3b8 !important;
  padding: 4px !important;
  width: 26px;
  height: 26px;
  border-radius: 4px !important;
  display: inline-flex !important;
  align-items: center !important;
  justify-content: center !important;
  cursor: pointer !important;
  transition: all 0.2s ease !important;
  line-height: 1;
}

.btn-edit-ghost:hover {
  color: #1e40af !important;
  background: rgba(30, 64, 175, 0.08) !important;
}

.icon-edit-ghost {
  width: 14px !important;
  height: 14px !important;
}

.btn-edit-inline {
  background: #eff6ff !important;
  border: 1px solid rgba(37, 99, 235, 0.25) !important;
  color: #1e40af !important;
  font-size: 0.78rem !important;
  font-weight: 700 !important;
  border-radius: 6px !important;
  padding: 3px 8px !important;
  display: inline-flex !important;
  align-items: center !important;
  gap: 4px !important;
  cursor: pointer !important;
  transition: all 0.2s ease !important;
}

.btn-edit-inline:hover {
  background: #1e40af !important;
  color: #ffffff !important;
  border-color: #1e40af !important;
}

.profile-details-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  flex: 1;
}

.profile-detail-cell {
  padding: 18px 24px;
  border-bottom: 1px solid rgba(37, 99, 235, 0.06);
  display: flex;
  flex-direction: column;
  gap: 4px;
  justify-content: center;
}

.profile-detail-cell:nth-child(odd) {
  border-right: 1px solid rgba(37, 99, 235, 0.06);
}

.profile-detail-cell:last-child,
.profile-detail-cell:nth-last-child(2) {
  border-bottom: none;
}

.profile-detail-cell:last-child:nth-child(odd) {
  grid-column: span 2;
  border-right: none;
}

.profile-detail-label {
  font-size: 0.78rem;
  font-weight: 700;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.profile-detail-value {
  font-size: 0.96rem;
  font-weight: 600;
  color: #1e293b;
  word-break: break-word;
}

.btn-save-modal {
  background: #1e40af !important;
  border-color: #1e40af !important;
  color: #ffffff !important;
  font-weight: 700;
}

.btn-save-modal:hover {
  background: #1e3a8a !important;
}

/* ===== Avatar Edit Modal Elements ===== */
.avatar-preview-wrapper {
  position: relative;
  width: 96px;
  height: 96px;
  border-radius: 50%;
  border: 3px solid #1e40af;
  box-shadow: 0 4px 12px rgba(37, 99, 235, 0.15);
  background: #ffffff;
  cursor: pointer;
  overflow: visible;
  transition: transform 0.2s ease;
}

.avatar-preview-wrapper:hover {
  transform: scale(1.04);
}

.avatar-preview-img {
  width: 100%;
  height: 100%;
  border-radius: 50%;
  object-fit: cover;
}

.avatar-camera-btn {
  position: absolute;
  bottom: -2px;
  right: -2px;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: #1e40af;
  border: 2px solid #ffffff;
  color: #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.2);
  transition: all 0.2s ease;
}

.avatar-camera-btn:hover {
  background: #1e3a8a;
  transform: scale(1.1);
}

.preset-avatar-btn {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  border: 2px solid transparent;
  padding: 0;
  background: none;
  cursor: pointer;
  overflow: hidden;
  transition: all 0.2s ease;
  margin: 0 3px;
}

.preset-avatar-btn:hover {
  transform: scale(1.12);
  border-color: rgba(37, 99, 235, 0.4);
}

.preset-avatar-btn.active {
  border-color: #1e40af;
  box-shadow: 0 0 0 2px rgba(37, 99, 235, 0.3);
  transform: scale(1.1);
}

.preset-avatar-thumb {
  width: 100%;
  height: 100%;
  border-radius: 50%;
  object-fit: cover;
}

/* ===== Responsive Breakpoints ===== */
@media (max-width: 992px) {
  .profile-unified-body {
    flex-direction: column;
  }

  .profile-identity-col {
    flex: 1 1 auto;
    max-width: 100%;
    border-right: none;
    border-bottom: 1px solid rgba(37, 99, 235, 0.1);
    padding: 26px 20px;
  }

  .profile-info-col {
    flex: 1 1 auto;
    max-width: 100%;
  }
}

@media (max-width: 768px) {
  .dashboard-topbar {
    min-height: auto;
    padding: 14px 16px;
    margin-bottom: 16px;
  }

  .dashboard-page-title {
    font-size: 1.2rem;
    gap: 8px;
  }

  .dashboard-topbar-subtitle {
    font-size: 0.8rem;
  }

  .dashboard-title-icon {
    width: 22px;
    height: 22px;
    font-size: 1.2rem;
  }

  .profile-content-container {
    padding: 0 12px;
  }

  .profile-avatar-wrapper {
    width: 88px;
    height: 88px;
    margin-bottom: 12px;
  }

  .profile-name {
    font-size: 1.28rem;
    margin-bottom: 8px;
  }

  .profile-info-header {
    padding: 14px 18px;
  }

  .profile-info-title {
    font-size: 1.02rem;
  }

  .btn-edit-header {
    font-size: 0.8rem;
    padding: 5px 12px;
  }

  /* Force clean single column list on mobile screens */
  .profile-details-grid {
    display: flex !important;
    flex-direction: column !important;
  }

  .profile-detail-cell {
    width: 100% !important;
    grid-column: auto !important;
    border-right: none !important;
    border-bottom: 1px solid rgba(37, 99, 235, 0.08) !important;
    padding: 14px 18px !important;
  }

  .profile-detail-cell:last-child {
    border-bottom: none !important;
  }

  .profile-detail-label {
    font-size: 0.74rem;
    margin-bottom: 2px;
  }

  .profile-detail-value {
    font-size: 0.94rem;
    line-height: 1.35;
    word-break: break-word;
  }
}

@media (max-width: 480px) {
  .profile-content-container {
    padding: 0 8px;
  }

  .profile-identity-col {
    padding: 22px 14px;
  }

  .profile-avatar-wrapper {
    width: 80px;
    height: 80px;
    margin-bottom: 10px;
  }

  .profile-name {
    font-size: 1.18rem;
  }

  .profile-info-header {
    padding: 12px 14px;
  }

  .profile-detail-cell {
    padding: 12px 14px !important;
  }
}
</style>
